import React, { useState, useEffect } from 'react';
import { 
  Package, CheckCircle2, Circle, Clock, User, MapPin, 
  Barcode, ShoppingBag, ArrowRight, RefreshCw, Search,
  ScanLine, CheckCheck, Timer, AlertCircle
} from 'lucide-react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import BackButton from '../components/BackButton';
import CameraBarcodeScanner from '../components/CameraBarcodeScanner';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const PickingList = ({ onBack, user }) => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [showScanner, setShowScanner] = useState(false);
  const [currentItemToScan, setCurrentItemToScan] = useState(null);

  const token = localStorage.getItem('auth_token');

  // Fetch picking orders
  const fetchOrders = async () => {
    try {
      const res = await fetch(`${API_URL}/api/picking/orders`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!res.ok) throw new Error('Failed to fetch orders');
      const data = await res.json();
      setOrders(data);
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOrders();
    // Poll every 30 seconds
    const interval = setInterval(fetchOrders, 30000);
    return () => clearInterval(interval);
  }, []);

  // Start picking an order
  const startPicking = async (orderId) => {
    try {
      const res = await fetch(`${API_URL}/api/picking/orders/${orderId}/start`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!res.ok) throw new Error('Failed to start picking');
      toast.success('Picking started!');
      fetchOrders();
      
      // Auto-select this order
      const order = orders.find(o => o.id === orderId);
      if (order) {
        setSelectedOrder({ ...order, status: 'picking' });
      }
    } catch (error) {
      toast.error(error.message);
    }
  };

  // Pick an item
  const pickItem = async (orderId, productId) => {
    try {
      const res = await fetch(`${API_URL}/api/picking/orders/${orderId}/pick-item?product_id=${productId}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!res.ok) throw new Error('Failed to pick item');
      const data = await res.json();
      
      // Update local state
      if (selectedOrder) {
        const updatedItems = selectedOrder.items.map(item => 
          item.product_id === productId ? { ...item, picked: true } : item
        );
        setSelectedOrder({ ...selectedOrder, items: updatedItems });
      }
      
      toast.success('Item picked!');
      
      if (data.all_picked) {
        toast.success('All items picked! Ready to complete.');
      }
    } catch (error) {
      toast.error(error.message);
    }
  };

  // Complete picking
  const completePicking = async (orderId) => {
    try {
      const res = await fetch(`${API_URL}/api/picking/orders/${orderId}/complete`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!res.ok) throw new Error('Failed to complete picking');
      const data = await res.json();
      
      toast.success(`Order completed! ${data.pickup_code ? `Pickup code: ${data.pickup_code}` : 'Ready for delivery.'}`);
      setSelectedOrder(null);
      fetchOrders();
    } catch (error) {
      toast.error(error.message);
    }
  };

  // Handle barcode scan
  const handleBarcodeScan = (barcode) => {
    if (selectedOrder && currentItemToScan) {
      // Verify the barcode matches the expected item
      const item = selectedOrder.items.find(i => i.product_id === currentItemToScan);
      if (item) {
        // For now, accept any scan as confirmation
        pickItem(selectedOrder.id, currentItemToScan);
        setShowScanner(false);
        setCurrentItemToScan(null);
      }
    }
  };

  // Filter orders
  const filteredOrders = orders.filter(order => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      order.id.toLowerCase().includes(query) ||
      order.delivery_info?.name?.toLowerCase().includes(query)
    );
  });

  // Calculate progress
  const getProgress = (items) => {
    if (!items || items.length === 0) return 0;
    const picked = items.filter(i => i.picked).length;
    return Math.round((picked / items.length) * 100);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <RefreshCw className="w-12 h-12 animate-spin text-blue-500 mx-auto mb-4" />
          <p className="text-gray-500">Loading picking orders...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <BackButton onClick={onBack} />
              <div>
                <h1 className="text-xl sm:text-2xl font-bold text-gray-900 flex items-center gap-2">
                  <Package className="w-6 h-6 text-orange-500" />
                  Picking List
                </h1>
                <p className="text-gray-500 text-sm">{orders.length} orders to pick</p>
              </div>
            </div>
            <Button variant="outline" onClick={fetchOrders} data-testid="refresh-picking-btn">
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-6">
        <div className="grid lg:grid-cols-2 gap-6">
          {/* Orders List */}
          <div>
            <div className="mb-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  placeholder="Search orders..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                  data-testid="search-picking-orders"
                />
              </div>
            </div>

            <div className="space-y-3">
              {filteredOrders.length === 0 ? (
                <Card className="p-8 text-center">
                  <CheckCheck className="w-12 h-12 text-green-500 mx-auto mb-2" />
                  <p className="text-gray-500">All orders picked! Great job!</p>
                </Card>
              ) : (
                filteredOrders.map(order => (
                  <Card 
                    key={order.id} 
                    className={`p-4 cursor-pointer transition-all hover:shadow-md ${
                      selectedOrder?.id === order.id ? 'ring-2 ring-orange-500' : ''
                    }`}
                    onClick={() => setSelectedOrder(order)}
                    data-testid={`picking-order-${order.id}`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="font-bold text-gray-900">
                            #{order.order_number || order.id.slice(0, 8).toUpperCase()}
                          </span>
                          <Badge 
                            className={order.status === 'picking' ? 'bg-orange-500' : 'bg-blue-500'}
                          >
                            {order.status === 'picking' ? 'Picking' : 'Ready'}
                          </Badge>
                          {order.order_type === 'pickup' && (
                            <Badge variant="outline">Store Pickup</Badge>
                          )}
                        </div>
                        <p className="text-sm text-gray-600 flex items-center gap-1">
                          <User className="w-3 h-3" />
                          {order.delivery_info?.name}
                        </p>
                        <p className="text-sm text-gray-500 flex items-center gap-1">
                          <ShoppingBag className="w-3 h-3" />
                          {order.items?.length || 0} items · ${order.total?.toFixed(2)}
                        </p>
                      </div>
                      
                      {order.status === 'picking' && (
                        <div className="text-right">
                          <div className="text-2xl font-bold text-orange-500">
                            {getProgress(order.items)}%
                          </div>
                          <div className="text-xs text-gray-500">picked</div>
                        </div>
                      )}
                    </div>

                    {order.status === 'payment_confirmed' && (
                      <Button 
                        className="w-full mt-3 bg-orange-500 hover:bg-orange-600"
                        onClick={(e) => {
                          e.stopPropagation();
                          startPicking(order.id);
                        }}
                        data-testid={`start-picking-${order.id}`}
                      >
                        <Timer className="w-4 h-4 mr-2" />
                        Start Picking
                      </Button>
                    )}
                  </Card>
                ))
              )}
            </div>
          </div>

          {/* Selected Order Details */}
          <div>
            {selectedOrder ? (
              <Card className="p-4 sticky top-24">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-lg font-bold">
                    Order #{selectedOrder.order_number || selectedOrder.id.slice(0, 8).toUpperCase()}
                  </h2>
                  <Badge className={selectedOrder.status === 'picking' ? 'bg-orange-500' : 'bg-blue-500'}>
                    {selectedOrder.status}
                  </Badge>
                </div>

                {/* Customer Info */}
                <div className="bg-gray-50 rounded-lg p-3 mb-4">
                  <p className="font-medium">{selectedOrder.delivery_info?.name}</p>
                  <p className="text-sm text-gray-600">{selectedOrder.delivery_info?.phone}</p>
                  {selectedOrder.order_type === 'delivery' && (
                    <p className="text-sm text-gray-500 flex items-center gap-1 mt-1">
                      <MapPin className="w-3 h-3" />
                      {selectedOrder.delivery_info?.address}, {selectedOrder.delivery_info?.city}
                    </p>
                  )}
                </div>

                {/* Items List */}
                <div className="space-y-2 mb-4 max-h-[400px] overflow-y-auto">
                  <h3 className="font-medium text-gray-700 flex items-center gap-2">
                    <ShoppingBag className="w-4 h-4" />
                    Items to Pick ({selectedOrder.items?.length || 0})
                  </h3>
                  
                  {selectedOrder.items?.map((item, idx) => (
                    <div 
                      key={idx}
                      className={`flex items-center gap-3 p-3 rounded-lg border ${
                        item.picked 
                          ? 'bg-green-50 border-green-200' 
                          : 'bg-white border-gray-200'
                      }`}
                    >
                      <button
                        onClick={() => {
                          if (!item.picked && selectedOrder.status === 'picking') {
                            pickItem(selectedOrder.id, item.product_id);
                          }
                        }}
                        disabled={item.picked || selectedOrder.status !== 'picking'}
                        className="flex-shrink-0"
                        data-testid={`pick-item-${item.product_id}`}
                      >
                        {item.picked ? (
                          <CheckCircle2 className="w-6 h-6 text-green-500" />
                        ) : (
                          <Circle className="w-6 h-6 text-gray-300 hover:text-orange-500" />
                        )}
                      </button>
                      
                      <div className="flex-1 min-w-0">
                        <p className={`font-medium truncate ${item.picked ? 'line-through text-gray-400' : ''}`}>
                          {item.name}
                        </p>
                        <p className="text-sm text-gray-500">
                          Qty: {item.quantity} · ${item.price.toFixed(2)}
                        </p>
                      </div>

                      {selectedOrder.status === 'picking' && !item.picked && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => {
                            setCurrentItemToScan(item.product_id);
                            setShowScanner(true);
                          }}
                          data-testid={`scan-item-${item.product_id}`}
                        >
                          <ScanLine className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  ))}
                </div>

                {/* Progress */}
                {selectedOrder.status === 'picking' && (
                  <div className="mb-4">
                    <div className="flex justify-between text-sm mb-1">
                      <span>Progress</span>
                      <span>{getProgress(selectedOrder.items)}%</span>
                    </div>
                    <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                      <div 
                        className="h-full bg-orange-500 transition-all duration-300"
                        style={{ width: `${getProgress(selectedOrder.items)}%` }}
                      />
                    </div>
                  </div>
                )}

                {/* Complete Button */}
                {selectedOrder.status === 'picking' && getProgress(selectedOrder.items) === 100 && (
                  <Button 
                    className="w-full bg-green-500 hover:bg-green-600"
                    onClick={() => completePicking(selectedOrder.id)}
                    data-testid="complete-picking-btn"
                  >
                    <CheckCheck className="w-4 h-4 mr-2" />
                    Complete Picking
                  </Button>
                )}

                {selectedOrder.status === 'payment_confirmed' && (
                  <Button 
                    className="w-full bg-orange-500 hover:bg-orange-600"
                    onClick={() => startPicking(selectedOrder.id)}
                  >
                    <Timer className="w-4 h-4 mr-2" />
                    Start Picking This Order
                  </Button>
                )}
              </Card>
            ) : (
              <Card className="p-8 text-center sticky top-24">
                <Package className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-500">Select an order to start picking</p>
              </Card>
            )}
          </div>
        </div>
      </div>

      {/* Barcode Scanner Modal */}
      <CameraBarcodeScanner
        isOpen={showScanner}
        onClose={() => {
          setShowScanner(false);
          setCurrentItemToScan(null);
        }}
        onScan={handleBarcodeScan}
        mode="verify"
      />
    </div>
  );
};

export default PickingList;
